import { useLingui } from '@lingui/react/macro';
import { Toast } from '@repo/videa-components/src/index';
import { NoteEditor, type NoteEditorProps } from '@repo/videa-notes';
import { toast } from 'sonner';

import { FeedbackWidget } from '@/common-components/elements/FeedbackWidget/FeedbackWidget';
import { useSubmitFeedback } from '@/react-query/videa-notes/useSubmitFeedback';

import { useNoteHandlers } from '../context/VideaNotesNoteResultContext';

interface NoteEditorWrapperProps
  extends Omit<NoteEditorProps, 'feedbackWidget' | 't'> {
  existingRating?: 'up' | 'down';
  onFeedbackSubmitted?: (rating: 'up' | 'down') => void;
}

export const NoteEditorWrapper = ({
  onFeedbackSubmitted,
  ...noteEditorProps
}: NoteEditorWrapperProps) => {
  const { onVariableValueChange } = useNoteHandlers();
  const { t } = useLingui();
  const submitFeedbackMutation = useSubmitFeedback();

  const upOptions = [
    { value: 'accurate-information', label: t`Accurate information` },
    { value: 'minimal-edits', label: t`Minimal edits needed` },
    { value: 'saved-time', label: t`Saved time` },
    { value: 'correct-terminology', label: t`Correct terminology` },
  ];

  const downOptions = [
    { value: 'inaccurate', label: t`Inaccurate information` },
    { value: 'incomplete', label: t`Missing information` },
    { value: 'poor-formatting', label: t`Poor formatting` },
    { value: 'confusing', label: t`Confusing or unclear` },
  ];

  const handleFeedback = async (
    rating: 'up' | 'down',
    selectedOptions: string[],
    additionalComments?: string,
  ) => {
    try {
      if (noteEditorProps.noteId) {
        const backendRating = rating;

        let feedbackText: string | undefined;
        if (selectedOptions.length > 0 || additionalComments) {
          const parts: string[] = [];

          if (selectedOptions.length > 0) {
            parts.push(`Selected: ${selectedOptions.join(', ')}`);
          }

          if (additionalComments && additionalComments.trim()) {
            parts.push(additionalComments.trim());
          }

          feedbackText = parts.join('\n');
        }

        await submitFeedbackMutation.mutateAsync({
          clinicalNoteId: noteEditorProps.noteId,
          feedbackBody: {
            rating: backendRating,
            feedback: feedbackText,
          },
        });

        // Notify parent component about successful feedback submission
        onFeedbackSubmitted?.(backendRating);
      }
    } catch (error) {
      console.error('Failed to submit feedback:', error);
      toast(
        <Toast
          variant="error"
          title={t`Failed to submit feedback`}
          description={t`There was an error submitting your feedback. Please try again.`}
        />,
        {
          closeButton: true,
          position: 'top-right',
          style: { width: '460px' },
        },
      );
      throw error;
    }
  };

  const feedbackWidget =
    noteEditorProps.noteId && noteEditorProps.feedbackTraceId ? (
      <FeedbackWidget
        label={t`Rate this note:`}
        upOptions={upOptions}
        downOptions={downOptions}
        variant="minimalist"
        existingRating={noteEditorProps.existingRating}
        onRate={handleFeedback}
      />
    ) : undefined;

  return (
    <NoteEditor
      {...noteEditorProps}
      feedbackWidget={feedbackWidget}
      t={(key: string) => t(key as any)}
      onVariableValueChange={onVariableValueChange}
    />
  );
};
